import { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Loader2, Upload, X } from 'lucide-react';
import { useLanguage } from '../../lib/i18n/LanguageContext';
import SectionTitle from '../../components/ui/SectionTitle';
import Button from '../../components/ui/Button';

interface ApplicationFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  position: string;
  whyWorkWithUs: string;
  resumeFile: File | null;
  coverLetterFile: File | null;
  gdprConsent: boolean;
}

interface FormStatus {
  type: 'success' | 'error' | null;
  message: string;
  isSubmitting: boolean;
}

interface ValidationErrors {
  [key: string]: string;
}

const CareersApplicationForm = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState<ApplicationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    whyWorkWithUs: '',
    resumeFile: null,
    coverLetterFile: null,
    gdprConsent: false,
  });
  const [formStatus, setFormStatus] = useState<FormStatus>({
    type: null,
    message: '',
    isSubmitting: false,
  });
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Clear form status when user starts typing
    if (formStatus.type) {
      setFormStatus({ type: null, message: '', isSubmitting: false });
    }
  };

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    fileType: 'resume' | 'coverLetter'
  ) => {
    const file = e.target.files?.[0] || null;
    const fieldName = fileType === 'resume' ? 'resumeFile' : 'coverLetterFile';

    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ];
      if (!allowedTypes.includes(file.type)) {
        setValidationErrors(prev => ({
          ...prev,
          [fieldName]: t.careers.application.form.validation.fileType,
        }));
        return;
      }

      // Validate file size
      const maxSize = fileType === 'resume' ? 5 * 1024 * 1024 : 2 * 1024 * 1024; // 5MB for resume, 2MB for cover letter
      if (file.size > maxSize) {
        const maxSizeMB = fileType === 'resume' ? '5MB' : '2MB';
        setValidationErrors(prev => ({
          ...prev,
          [fieldName]: t.careers.application.form.validation.fileSize.replace('{size}', maxSizeMB),
        }));
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [fieldName]: file,
    }));

    // Clear validation error for this field
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const removeFile = (fileType: 'resume' | 'coverLetter') => {
    const fieldName = fileType === 'resume' ? 'resumeFile' : 'coverLetterFile';
    setFormData(prev => ({
      ...prev,
      [fieldName]: null,
    }));
  };

  const validateForm = (): boolean => {
    const errors: ValidationErrors = {};

    // Required field validations
    if (!formData.firstName.trim()) {
      errors.firstName = t.careers.application.form.validation.required;
    }
    if (!formData.lastName.trim()) {
      errors.lastName = t.careers.application.form.validation.required;
    }
    if (!formData.email.trim()) {
      errors.email = t.careers.application.form.validation.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = t.careers.application.form.validation.emailFormat;
    }
    if (!formData.position) {
      errors.position = t.careers.application.form.validation.required;
    }
    if (!formData.whyWorkWithUs.trim()) {
      errors.whyWorkWithUs = t.careers.application.form.validation.required;
    } else if (formData.whyWorkWithUs.length > 500) {
      errors.whyWorkWithUs = t.careers.application.form.validation.maxLength;
    }
    if (!formData.resumeFile) {
      errors.resumeFile = t.careers.application.form.validation.resumeRequired;
    }
    if (!formData.gdprConsent) {
      errors.gdprConsent = t.careers.application.form.validation.gdprRequired;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formStatus.isSubmitting) return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    setFormStatus({ type: null, message: '', isSubmitting: true });

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured');
      }

      // Helper function to convert File to base64
      const fileToBase64 = (
        file: File
      ): Promise<{ content: string; contentType: string; name: string }> => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
            const base64Content = result.split(',')[1];
            resolve({
              content: base64Content,
              contentType: file.type,
              name: file.name,
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      };

      // Prepare file data
      let resumeFileData = null;
      let coverLetterFileData = null;

      if (formData.resumeFile) {
        resumeFileData = await fileToBase64(formData.resumeFile);
      }

      if (formData.coverLetterFile) {
        coverLetterFileData = await fileToBase64(formData.coverLetterFile);
      }

      // Prepare application data
      const applicationData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim() || undefined,
        position: formData.position,
        whyWorkWithUs: formData.whyWorkWithUs.trim(),
        resumeFile: resumeFileData,
        coverLetterFile: coverLetterFileData,
        gdprConsent: formData.gdprConsent,
      };

      const response = await fetch(`${supabaseUrl}/functions/v1/send-application-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify(applicationData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        window.umami?.track('form-submit', {
          form: 'application',
          source: window.location.pathname,
        });

        setFormStatus({
          type: 'success',
          message: result.message || t.careers.application.form.successMessage,
          isSubmitting: false,
        });

        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          position: '',
          whyWorkWithUs: '',
          resumeFile: null,
          coverLetterFile: null,
          gdprConsent: false,
        });
      } else {
        throw new Error(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setFormStatus({
        type: 'error',
        message: error instanceof Error ? error.message : t.careers.application.form.errorMessage,
        isSubmitting: false,
      });
    }
  };

  return (
    <section id="apply" className="py-20 bg-neutral-50">
      <div className="container-custom">
        <SectionTitle
          title={t.careers.application.title}
          subtitle={t.careers.application.subtitle}
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto mt-12"
        >
          <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8 space-y-6">
            {/* Form Status Message */}
            {formStatus.type && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg flex items-center gap-3 ${
                  formStatus.type === 'success'
                    ? 'bg-green-50 text-green-800 border border-green-200'
                    : 'bg-red-50 text-red-800 border border-red-200'
                }`}
              >
                {formStatus.type === 'success' ? (
                  <CheckCircle className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <p className="text-sm">{formStatus.message}</p>
              </motion.div>
            )}

            {/* Personal Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-neutral-700 mb-2">
                  {t.careers.application.form.firstName}*
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={formStatus.isSubmitting}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    validationErrors.firstName ? 'border-red-500' : 'border-neutral-300'
                  }`}
                  placeholder={t.careers.application.form.placeholders.firstName}
                />
                {validationErrors.firstName && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-neutral-700 mb-2">
                  {t.careers.application.form.lastName}*
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={formStatus.isSubmitting}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    validationErrors.lastName ? 'border-red-500' : 'border-neutral-300'
                  }`}
                  placeholder={t.careers.application.form.placeholders.lastName}
                />
                {validationErrors.lastName && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.lastName}</p>
                )}
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-neutral-700 mb-2">
                  {t.careers.application.form.email}*
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={formStatus.isSubmitting}
                  className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                    validationErrors.email ? 'border-red-500' : 'border-neutral-300'
                  }`}
                  placeholder={t.careers.application.form.placeholders.email}
                />
                {validationErrors.email && (
                  <p className="text-red-600 text-sm mt-1">{validationErrors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-neutral-700 mb-2">
                  {t.careers.application.form.phone}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={formStatus.isSubmitting}
                  className="w-full px-4 py-3 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder={t.careers.application.form.placeholders.phone}
                />
              </div>
            </div>

            {/* Position of Interest */}
            <div>
              <label htmlFor="position" className="block text-neutral-700 mb-2">
                {t.careers.application.form.position}*
              </label>
              <select
                id="position"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                disabled={formStatus.isSubmitting}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${
                  validationErrors.position ? 'border-red-500' : 'border-neutral-300'
                }`}
              >
                <option value="">{t.careers.application.form.placeholders.position}</option>
                {t.careers.application.form.positions.map((position, index) => (
                  <option key={index} value={position}>
                    {position}
                  </option>
                ))}
              </select>
              {validationErrors.position && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.position}</p>
              )}
            </div>

            {/* Resume Upload */}
            <div>
              <label className="block text-neutral-700 mb-2">
                {t.careers.application.form.resume}*
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center ${
                  validationErrors.resumeFile ? 'border-red-500' : 'border-neutral-300'
                }`}
              >
                {formData.resumeFile ? (
                  <div className="flex items-center justify-between bg-neutral-50 p-3 rounded">
                    <div className="flex items-center gap-2">
                      <Upload className="w-4 h-4 text-neutral-500" />
                      <span className="text-sm text-neutral-700">{formData.resumeFile.name}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile('resume')}
                      className="text-red-500 hover:text-red-700"
                      disabled={formStatus.isSubmitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                    <p className="text-neutral-600 mb-2">{t.careers.application.form.resumeHelp}</p>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={e => handleFileChange(e, 'resume')}
                      disabled={formStatus.isSubmitting}
                      className="hidden"
                      id="resume-upload"
                    />
                    <label
                      htmlFor="resume-upload"
                      className="inline-block px-4 py-2 bg-neutral-100 text-neutral-700 rounded cursor-pointer hover:bg-neutral-200 transition-colors"
                    >
                      {t.careers.application.form.chooseFile}
                    </label>
                  </div>
                )}
              </div>
              {validationErrors.resumeFile && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.resumeFile}</p>
              )}
            </div>

            {/* Cover Letter Upload */}
            <div>
              <label className="block text-neutral-700 mb-2">
                {t.careers.application.form.coverLetter}
              </label>
              <div className="border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center">
                {formData.coverLetterFile ? (
                  <div className="flex items-center justify-between bg-neutral-50 p-3 rounded">
                    <div className="flex items-center gap-2">
                      <Upload className="w-4 h-4 text-neutral-500" />
                      <span className="text-sm text-neutral-700">
                        {formData.coverLetterFile.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile('coverLetter')}
                      className="text-red-500 hover:text-red-700"
                      disabled={formStatus.isSubmitting}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
                    <p className="text-neutral-600 mb-2">
                      {t.careers.application.form.coverLetterHelp}
                    </p>
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={e => handleFileChange(e, 'coverLetter')}
                      disabled={formStatus.isSubmitting}
                      className="hidden"
                      id="cover-letter-upload"
                    />
                    <label
                      htmlFor="cover-letter-upload"
                      className="inline-block px-4 py-2 bg-neutral-100 text-neutral-700 rounded cursor-pointer hover:bg-neutral-200 transition-colors"
                    >
                      {t.careers.application.form.chooseFile}
                    </label>
                  </div>
                )}
              </div>
              {validationErrors.coverLetterFile && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.coverLetterFile}</p>
              )}
            </div>

            {/* Why Work With Us */}
            <div>
              <label htmlFor="whyWorkWithUs" className="block text-neutral-700 mb-2">
                {t.careers.application.form.whyWorkWithUs}*
              </label>
              <textarea
                id="whyWorkWithUs"
                name="whyWorkWithUs"
                value={formData.whyWorkWithUs}
                onChange={handleInputChange}
                rows={4}
                maxLength={500}
                disabled={formStatus.isSubmitting}
                className={`w-full px-4 py-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 resize-none ${
                  validationErrors.whyWorkWithUs ? 'border-red-500' : 'border-neutral-300'
                }`}
                placeholder={t.careers.application.form.placeholders.whyWorkWithUs}
              ></textarea>
              <div className="flex justify-between items-center mt-1">
                {validationErrors.whyWorkWithUs ? (
                  <p className="text-red-600 text-sm">{validationErrors.whyWorkWithUs}</p>
                ) : (
                  <div></div>
                )}
                <p className="text-neutral-500 text-sm">{formData.whyWorkWithUs.length}/500</p>
              </div>
            </div>

            {/* GDPR Consent */}
            <motion.div whileTap={{ scale: 0.98 }} transition={{ duration: 0.1 }}>
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="gdprConsent"
                  checked={formData.gdprConsent}
                  onChange={handleInputChange}
                  disabled={formStatus.isSubmitting}
                  className={`mt-1 w-5 h-5 text-red-600 border-2 rounded focus:ring-red-500 ${
                    validationErrors.gdprConsent ? 'border-red-500' : 'border-neutral-300'
                  }`}
                />
                <span className="text-neutral-700 text-base leading-relaxed">
                  {t.careers.application.form.gdprConsent}*
                </span>
              </label>
              {validationErrors.gdprConsent && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.gdprConsent}</p>
              )}
            </motion.div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                disabled={formStatus.isSubmitting}
                className="w-full"
              >
                {formStatus.isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t.careers.application.form.submitting}
                  </span>
                ) : (
                  t.careers.application.form.submitButton
                )}
              </Button>
              {!formStatus.type && (
                <p className="text-sm text-neutral-500 mt-3 text-center">
                  {t.careers.application.form.note}
                </p>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

export default CareersApplicationForm;
